export const SET_TEMPO_STATUS = 0x51;
const TIME_SIGNATURE_STATUS = 0x58;

// Läser av taktartsdata direkt från spårdatan: numerator/denominator som i
// t.ex. "3/4" eller "6/8", samt "MIDI clocks per metronome click" (24 clocks
// = 1 fjärdedelsnot) som filen själv kan använda för att ange vad en
// metronomklick faktiskt motsvarar (t.ex. en punkterad fjärdedel i 6/8).
// Faller tillbaka på MIDI-standardens defaultvärden (4/4, klick var
// fjärdedel) om inget Time Signature-event hittas.
export function getMidiTimeSignature(midi) {
  const DEFAULT = { numerator: 4, denominator: 4, clocksPerClick: 24 };
  if (!midi?.tracks?.length) return DEFAULT;

  let earliest = null;
  midi.tracks.forEach(track => {
    track.forEach(ev => {
      if (ev.messageStatusByte === TIME_SIGNATURE_STATUS && (!earliest || ev.ticks < earliest.ticks)) {
        earliest = ev;
      }
    });
  });

  if (!earliest) return DEFAULT;
  const data = earliest.messageData;
  return {
    numerator: data[0] || DEFAULT.numerator,
    denominator: 2 ** data[1] || DEFAULT.denominator,
    clocksPerClick: data[2] || DEFAULT.clocksPerClick
  };
}

// Räknar ut hur många inräkningsklick en takt ska ha och hur långt det är
// mellan dem (i sekunder), givet filens taktart och effektiv BPM (BPM anges
// alltid i fjärdedelar per minut, oavsett taktartens nämnare). Ett klick är
// "clocksPerClick / 24" fjärdedelar långt. Standarddefaulten 24 (klick =
// en fjärdedel) ger t.ex. 3 klick för 6/8 (3 fjärdedelar per takt totalt);
// anger filen istället clocksPerClick=36 (klick = en punkterad fjärdedel,
// vanligt för sammansatt taktart i DAW:ar) blir det 2 klick istället.
export function getCountInPlan(midi, bpm) {
  const { numerator, denominator, clocksPerClick } = getMidiTimeSignature(midi);
  const barQuarterNotes = (numerator * 4) / denominator;
  const clickQuarterNotes = clocksPerClick / 24;
  const beats = Math.max(1, Math.round(barQuarterNotes / clickQuarterNotes));
  const beatSeconds = clickQuarterNotes * (60 / bpm);
  return { beats, beatSeconds };
}

// Läser av den effektiva starttempot direkt från spårdatan (inte midi.tempoChanges,
// som alltid innehåller en kvarvarande default-post på 120 BPM och kan ackumulera
// dubbletter över flera flush()-anrop i spessasynth_core).
export function getMidiBpm(midi) {
  if (!midi?.tracks?.length) return 120;

  let earliest = null;
  midi.tracks.forEach(track => {
    track.forEach(ev => {
      if (ev.messageStatusByte === SET_TEMPO_STATUS && (!earliest || ev.ticks < earliest.ticks)) {
        earliest = ev;
      }
    });
  });

  if (!earliest) return 120;
  const data = earliest.messageData;
  const usPerQuarter = (data[0] << 16) | (data[1] << 8) | data[2];
  return Math.round(60000000 / usPerQuarter);
}

// spessasynth_core's flush()/_parseInternal() re-scans the track data to rebuild
// derived fields, but a couple of them are running accumulators that are never
// reset first: lastVoiceEventTick only ever grows ("if (e.ticks > this.lastVoiceEventTick)"),
// and tempoChanges is pushed onto without clearing. So calling flush() a second
// time on an object whose event ticks have since been shifted DOWN (as
// normalizeMidi/transposeMidi do) leaves duration computed from the old, larger
// tick range - a MIDI that's really ~23s can end up reporting ~14 minutes.
// Reset those fields to their construction-time defaults before flushing so the
// rescan starts clean, same as a brand new MIDI() would.
export function safeFlushMidi(midi) {
  midi.lastVoiceEventTick = 0;
  midi.tempoChanges = [{ ticks: 0, tempo: 120 }];
  midi.flush();
}

// Sätter en enhetlig BPM för hela låten: uppdaterar det tidigaste Set Tempo-eventet,
// tar bort ev. övriga tempoändringar och injicerar ett nytt event om inget fanns.
export function setMidiBpm(midi, bpm) {
  const usPerQuarter = Math.max(1, Math.round(60000000 / bpm));
  const tempoData = new Uint8Array([
    (usPerQuarter >> 16) & 0xFF,
    (usPerQuarter >> 8) & 0xFF,
    usPerQuarter & 0xFF
  ]);

  const tempoEvents = [];
  midi.tracks.forEach((track, trackIndex) => {
    track.forEach(ev => {
      if (ev.messageStatusByte === SET_TEMPO_STATUS) {
        tempoEvents.push({ trackIndex, ev });
      }
    });
  });

  if (tempoEvents.length === 0) {
    midi.tracks[0].unshift({
      ticks: 0,
      messageStatusByte: SET_TEMPO_STATUS,
      messageData: tempoData
    });
  } else {
    tempoEvents.sort((a, b) => a.ev.ticks - b.ev.ticks);
    const [{ ev: keep }, ...rest] = tempoEvents;
    keep.ticks = 0;
    keep.messageData = tempoData;

    rest.forEach(({ trackIndex, ev }) => {
      const track = midi.tracks[trackIndex];
      const idx = track.indexOf(ev);
      if (idx !== -1) track.splice(idx, 1);
    });
  }

  // duration is derived from the tempo map at parse time, so it needs recomputing
  safeFlushMidi(midi);
}
